import React from "react";
import {Button, Input, Switch} from "antd";
import {
    Bug,
    Link2,
    Plus,
    Thermometer,
    Trash2,
    TriangleAlert,
} from "lucide-react";
import {useTranslation} from "react-i18next";

const telegramNotifyTypes = [
    {value: "log", labelKey: "errorLog", icon: Bug, iconColor: "grey", botLink: "fbox_warning_notifier_bot"},
    {value: "warning", labelKey: "warning", icon: TriangleAlert, iconColor: "orange", botLink: "fbox_warning_notifier_bot"},
    {value: "high_temp", labelKey: "highTemp", icon: Thermometer, iconColor: "red", botLink: "fbox_warning_notifier_bot"},
];

export const createEmptyTelegramGroup = () => ({
    group_id: "",
    notify_types: [],
});

export const normalizeTelegramGroupId = (value) => {
    const trimmedValue = String(value || "").trim();
    const digits = trimmedValue.replace(/\D/g, "");

    return trimmedValue.startsWith("-") ? `-${digits}` : digits;
};

const getTelegramBotUrl = (botLink) => {
    const value = String(botLink || "").trim();

    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;

    const normalizedBotName = value
        .replace(/^@/, "")
        .replace(/^t\.me\//i, "")
        .replace(/^telegram\.me\//i, "");

    return normalizedBotName ? `https://t.me/${normalizedBotName}` : "";
};

export const mapTelegramGroupsToFormData = (telegramGroups, fallback = []) => {
    const groups = Array.isArray(telegramGroups) ? telegramGroups : fallback;
    const normalizedGroups = groups
        .filter(Boolean)
        .map((group) => ({
            id: group.id,
            group_id: group.group_id || "",
            notify_types: Array.isArray(group.notify_types) ? group.notify_types : [],
        }));

    return normalizedGroups.length > 0 ? normalizedGroups : [createEmptyTelegramGroup()];
};

export const mapTelegramGroupsToPayload = (telegramGroups, deletedTelegramGroups = []) => [
    ...(Array.isArray(telegramGroups) ? telegramGroups : [])
        .map((group) => ({
            ...(group.id ? {id: group.id} : {}),
            group_id: normalizeTelegramGroupId(group.group_id),
            notify_types: Array.isArray(group.notify_types) ? group.notify_types : [],
        }))
        .filter((group) => /^-?\d+$/.test(group.group_id)),
    ...(Array.isArray(deletedTelegramGroups) ? deletedTelegramGroups : [])
        .map((group) => ({
            group_id: normalizeTelegramGroupId(group.group_id),
            delete: true,
        }))
        .filter((group) => /^-?\d+$/.test(group.group_id)),
];

const TelegramGroupSection = ({telegramGroups, onChange}) => {
    const {t} = useTranslation();
    const groups = telegramGroups || [createEmptyTelegramGroup()];

    const updateGroupId = (index, value) => {
        const groupId = normalizeTelegramGroupId(value);

        onChange(prev => ({
            ...prev,
            telegram_groups: (prev.telegram_groups || [createEmptyTelegramGroup()]).map((group, groupIndex) => (
                groupIndex === index
                    ? {...group, group_id: groupId}
                    : group
            ))
        }));
    };

    const updateNotifyType = (index, notifyType, checked) => {
        onChange(prev => ({
            ...prev,
            telegram_groups: (prev.telegram_groups || [createEmptyTelegramGroup()]).map((group, groupIndex) => {
                if (groupIndex !== index) return group;

                const notifyTypes = new Set(group.notify_types || []);

                if (checked) {
                    notifyTypes.add(notifyType);
                } else {
                    notifyTypes.delete(notifyType);
                }

                return {
                    ...group,
                    notify_types: Array.from(notifyTypes),
                };
            })
        }));
    };

    const addGroup = () => {
        onChange(prev => ({
            ...prev,
            telegram_groups: [
                ...(prev.telegram_groups || []),
                createEmptyTelegramGroup(),
            ],
        }));
    };

    const removeGroup = (index) => {
        onChange(prev => {
            const currentGroups = prev.telegram_groups || [createEmptyTelegramGroup()];
            const removedGroup = currentGroups[index];
            const removedGroupId = normalizeTelegramGroupId(removedGroup?.group_id);
            const shouldMarkDeleted = /^-?\d+$/.test(removedGroupId);

            return {
                ...prev,
                telegram_groups: currentGroups.length > 1
                    ? currentGroups.filter((_, groupIndex) => groupIndex !== index)
                    : [createEmptyTelegramGroup()],
                deleted_telegram_groups: shouldMarkDeleted
                    ? [
                        ...(prev.deleted_telegram_groups || []),
                        {group_id: removedGroupId},
                    ]
                    : prev.deleted_telegram_groups || [],
            };
        });
    };

    return (
        <div className="create-company__telegram-permissions">
            <div className="create-company__telegram-header">
                <span>{t("pages.companies.createColumns.telegram.title")}</span>
                <Button
                    className="create-company__telegram-add"
                    type="default"
                    size="small"
                    icon={<Plus size={14}/>}
                    onClick={addGroup}
                >
                    {t("pages.companies.createColumns.telegram.addGroup")}
                </Button>
            </div>
            <div className="create-company__telegram-rows">
                {groups.map((group, index) => (
                    <div
                        className="create-company__telegram-row"
                        key={group.id || index}
                    >
                        <Input
                            className="create-device__control create-company__telegram-input"
                            value={group.group_id}
                            onChange={(event) => updateGroupId(index, event.target.value)}
                            placeholder={t("pages.companies.createColumns.telegram.groupIdPlaceholder")}
                            inputMode="numeric"
                            pattern="-?[0-9]*"
                            allowClear
                        />

                        {telegramNotifyTypes.map((notifyType) => {
                            const NotifyIcon = notifyType.icon;
                            const botUrl = getTelegramBotUrl(notifyType.botLink);

                            return (
                                <div
                                    key={notifyType.value}
                                    className="create-company__telegram-notify-card"
                                >
                                    <div className="create-company__telegram-notify-heading">
                                        <span className="create-company__telegram-notify-icon">
                                            <NotifyIcon size={15} color={notifyType.iconColor}/>
                                        </span>
                                        <span className="create-company__telegram-notify-title">
                                            {t(`pages.companies.createColumns.telegram.notifyTypes.${notifyType.labelKey}`)}
                                        </span>
                                        <Button
                                            className="create-company__telegram-bot-link"
                                            type="link"
                                            size="small"
                                            href={botUrl || undefined}
                                            target="_blank"
                                            rel="noreferrer"
                                            disabled={!botUrl}
                                            icon={<Link2 size={14}/>}
                                            title={t("pages.companies.createColumns.telegram.botLink")}
                                            aria-label={t("pages.companies.createColumns.telegram.botLink")}
                                        />
                                        <Switch
                                            checked={(group.notify_types || []).includes(notifyType.value)}
                                            onChange={(checked) => updateNotifyType(index, notifyType.value, checked)}
                                        />
                                    </div>
                                </div>
                            );
                        })}

                        <Button
                            className="create-company__telegram-remove"
                            type="text"
                            size="small"
                            danger
                            icon={<Trash2 size={14}/>}
                            onClick={() => removeGroup(index)}
                            title={t("pages.companies.createColumns.telegram.removeGroup")}
                            aria-label={t("pages.companies.createColumns.telegram.removeGroup")}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TelegramGroupSection;
